// Routes.js - null-safe v2.1
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  MapContainer, TileLayer, Marker, Circle,
  Polyline, Popup, useMapEvents, useMap
} from 'react-leaflet';
import L from 'leaflet';
import { MAP_TILES, DEFAULT_TILE } from '../utils/mapTiles';

// ── Leaflet icon fix ───────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Icons ──────────────────────────────────────────────────────
const cpIcon = (num, color = '#4361ee') => L.divIcon({
  html: `<div style="background:${color};color:white;border:2.5px solid white;border-radius:50%;
    width:28px;height:28px;display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:12px;box-shadow:0 2px 8px rgba(0,0,0,.35);">${num}</div>`,
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
});

const pathDotIcon = (idx) => L.divIcon({
  html: `<div style="background:#f59e0b;color:white;border:2px solid white;border-radius:50%;
    width:20px;height:20px;display:flex;align-items:center;justify-content:center;
    font-weight:700;font-size:9px;box-shadow:0 1px 6px rgba(0,0,0,.35);">${idx + 1}</div>`,
  className: '', iconSize: [20, 20], iconAnchor: [10, 10],
});

const pendingIcon = L.divIcon({
  html: `<div style="background:#ef233c;color:white;border:3px solid white;border-radius:50%;
    width:32px;height:32px;display:flex;align-items:center;justify-content:center;
    font-size:16px;box-shadow:0 3px 14px rgba(0,0,0,.4);">📍</div>`,
  className: '', iconSize: [32, 32], iconAnchor: [16, 16],
});

const ROUTE_COLORS = ['#4361ee','#22c55e','#ef233c','#f59e0b','#8b5cf6','#06b6d4','#ec4899','#14b8a6'];
const GPS_PRESETS  = [
  { r: 20,  l: '🟢 Outdoor clear',   c: '#16a34a' },
  { r: 40,  l: '🟡 Outdoor partial', c: '#d97706' },
  { r: 60,  l: '🟠 Indoor low',      c: '#ea580c' },
  { r: 100, l: '🔴 Indoor high',     c: '#dc2626' },
];
const rColor = (r) => r<=25?'#16a34a':r<=50?'#d97706':r<=80?'#ea580c':'#dc2626';

const TILES = {
  street: MAP_TILES.googleStreet.url,
  satellite: MAP_TILES.googleHybrid.url,
  googleHybrid: MAP_TILES.googleHybrid.url,
  googleStreet: MAP_TILES.googleStreet.url,
  esriSatellite: MAP_TILES.esriSatellite.url,
  osm: MAP_TILES.osm.url,

};

// ── Map helpers ────────────────────────────────────────────────
function MapClick({ onMapClick, active }) {
  useMapEvents({ click: (e) => { if (active) onMapClick(e.latlng); } });
  return null;
}

function FlyTo({ center, zoom = 17 }) {
  const map = useMap();
  const prev = useRef(null);
  useEffect(() => {
    if (!center) return;
    const key = center.join(',');
    if (prev.current === key) return;
    prev.current = key;
    map.flyTo(center, zoom, { duration: 1.2 });
  }, [center]);
  return null;
}

function LocateBtn() {
  const map = useMap();
  return (
    <div style={{ position: 'absolute', bottom: 100, right: 12, zIndex: 1000 }}>
      <button onClick={() => {
        if (!navigator.geolocation) return toast.error('GPS not available');
        toast('📡 Getting location…', { duration: 1500 });
        navigator.geolocation.getCurrentPosition(
          p => { map.flyTo([p.coords.latitude, p.coords.longitude], 18, { duration: 1.2 }); toast.success('Moved to your location'); },
          () => toast.error('Could not get GPS')
        );
      }} title="My location" style={{
        width: 38, height: 38, borderRadius: 8, border: 'none', background: 'white',
        cursor: 'pointer', fontSize: 18, boxShadow: '0 2px 10px rgba(0,0,0,.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>🎯</button>
    </div>
  );
}

// ── Nominatim search box ───────────────────────────────────────
function SearchBox({ onResult }) {
  const [q, setQ]         = useState('');
  const [results, setRes] = useState([]);
  const [loading, setL]   = useState(false);
  const [open, setOpen]   = useState(false);
  const timer = useRef(null);

  const search = async (val) => {
    if (!val || val.length < 2) { setRes([]); setOpen(false); return; }
    setL(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(val)}&format=json&limit=6`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'PatrolTrack/1.0' } }
      );
      const d = await r.json();
      setRes(d); setOpen(d.length > 0);
    } catch { toast.error('Search failed'); }
    setL(false);
  };

  return (
    <div style={{ position: 'relative', zIndex: 20, marginBottom: 8 }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>🔍</span>
        <input value={q} onChange={e => { setQ(e.target.value); clearTimeout(timer.current); timer.current = setTimeout(() => search(e.target.value), 500); }}
          placeholder="Search plant, building, address…"
          style={{ width: '100%', padding: '10px 36px', border: '1.5px solid #e5e7eb', borderRadius: 9, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
        {loading && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#9ca3af' }}>⏳</span>}
        {q && !loading && <button onClick={() => { setQ(''); setRes([]); setOpen(false); }}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16 }}>✕</button>}
      </div>
      {open && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 9, marginTop: 3, boxShadow: '0 8px 24px rgba(0,0,0,.14)', border: '1px solid #e5e7eb', zIndex: 9999, maxHeight: 260, overflowY: 'auto' }}>
          {results.map((r, i) => {
            const parts = r.display_name.split(',');
            return (
              <div key={r.place_id} onClick={() => { setQ(parts.slice(0,2).join(', ')); setOpen(false); onResult({ lat: +r.lat, lng: +r.lon }); }}
                style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: i < results.length-1 ? '1px solid #f3f4f6' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background='#f0f4ff'}
                onMouseLeave={e => e.currentTarget.style.background='white'}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>📍 {parts[0]}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{parts.slice(1,3).join(', ')}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const emptyForm = { site_id:'', name:'', description:'', expected_duration_minutes:60, color:'#4361ee' };
const emptyCPForm = { name:'', description:'', radius_meters:60, sequence_order:1, lat:'', lng:'', scan_type:'photo' };

export default function Routes() {
  const [routes, setRoutes]       = useState([]);
  const [sites, setSites]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [routeForm, setRouteForm] = useState(emptyForm);
  const [showForm, setShowForm]   = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [checkpoints, setCheckpoints]     = useState([]);
  const [tile, setTile]           = useState(DEFAULT_TILE);

  // Map interaction modes
  const [mode, setMode]           = useState('none'); // 'place_cp' | 'draw_path' | 'none'
  const [pendingPin, setPendingPin] = useState(null);
  const [cpForm, setCpForm]       = useState(emptyCPForm);
  const [addingCP, setAddingCP]   = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);

  // Path drawing state
  const [pathPoints, setPathPoints] = useState([]); // [[lat,lng],...]
  const [savedPath, setSavedPath]   = useState([]); // loaded from DB
  const [savingPath, setSavingPath] = useState(false);
  const [deletingRouteId, setDeletingRouteId] = useState(null);

  // Drag-to-reorder
  const [dragging, setDragging]   = useState(null);
  const dragOver                  = useRef(null);

  const load = () =>
    Promise.all([axios.get('/api/routes'), axios.get('/api/sites')])
      .then(([r, s]) => { setRoutes(r.data); setSites(s.data); setLoading(false); });

  useEffect(() => { load(); }, []);

  const loadCheckpoints = async (routeId) => {
    const res = await axios.get(`/api/checkpoints/route/${routeId}`);
    setCheckpoints(res.data);
    // fly to first CP
    const firstWithCoords = res.data.find(cp => cp.latitude && cp.longitude);
    if (firstWithCoords) setFlyTarget([firstWithCoords.latitude, firstWithCoords.longitude]);
  };

  const openRoute = (route) => {
    setSelectedRoute(route);
    loadCheckpoints(route.id);
    setMode('none'); setPendingPin(null); setPathPoints([]);
    setCpForm(emptyCPForm); setFlyTarget(null);
    // Load saved path
    const geo = route.path_geojson;
    if (geo) {
      try {
        const parsed = typeof geo === 'string' ? JSON.parse(geo) : geo;
        const pts = parsed?.coordinates?.map(c => [c[1], c[0]]) || [];
        setSavedPath(pts);
      } catch { setSavedPath([]); }
    } else setSavedPath([]);
  };

  const closeRoute = () => {
    setSelectedRoute(null); setCheckpoints([]); setMode('none');
    setPendingPin(null); setPathPoints([]); setSavedPath([]);
  };

  // ── Checkpoint add ──────────────────────────────────────────
  const dropPin = useCallback((lat, lng) => {
    setPendingPin({ lat, lng });
    setCpForm(prev => ({ ...prev, lat: lat.toFixed(6), lng: lng.toFixed(6), sequence_order: checkpoints.length + 1 }));
    setMode('none');
  }, [checkpoints.length]);

  const applyManual = () => {
    const lat = parseFloat(cpForm.lat), lng = parseFloat(cpForm.lng);
    if (isNaN(lat)||isNaN(lng)) return toast.error('Invalid coordinates');
    setPendingPin({ lat, lng }); setFlyTarget([lat, lng]);
    toast.success('Pin placed');
  };

  const handleAddCP = async (e) => {
    e.preventDefault();
    if (cpForm.scan_type === 'gps' && !pendingPin) return toast.error('GPS location required for GPS-type checkpoints');
    setAddingCP(true);
    try {
      await axios.post('/api/checkpoints', {
        route_id: selectedRoute.id,
        name: cpForm.name, description: cpForm.description,
        latitude: pendingPin ? pendingPin.lat : null,
        longitude: pendingPin ? pendingPin.lng : null,
        radius_meters: +cpForm.radius_meters,
        sequence_order: +cpForm.sequence_order,
        scan_type: cpForm.scan_type || 'photo',
      });
      toast.success(`✅ "${cpForm.name}" added`);
      setCpForm(prev => ({ ...prev, name:'', description:'', lat:'', lng:'', sequence_order: checkpoints.length+2 }));
      setPendingPin(null);
      await loadCheckpoints(selectedRoute.id);
      load();
    } catch (err) { toast.error(err.response?.data?.error||'Failed'); }
    setAddingCP(false);
  };

  const deleteCP = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await axios.delete(`/api/checkpoints/${id}`);
    toast.success('Checkpoint removed');
    await loadCheckpoints(selectedRoute.id);
    load();
  };

  const handleDeleteRoute = async (route) => {
    if (!window.confirm(`Delete route "${route.name}"?\n\nThis will permanently remove the route, its drawn path, and all associated checkpoints.`)) return;
    setDeletingRouteId(route.id);
    try {
      await axios.delete(`/api/routes/${route.id}`);
      toast.success(`Route "${route.name}" deleted`);
      if (selectedRoute?.id === route.id) {
        closeRoute();
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete route');
    } finally {
      setDeletingRouteId(null);
    }
  };

  // ── Path drawing ─────────────────────────────────────────────
  const handleMapClick = (latlng) => {
    if (mode === 'place_cp') { dropPin(latlng.lat, latlng.lng); return; }
    if (mode === 'draw_path') {
      setPathPoints(prev => [...prev, [latlng.lat, latlng.lng]]);
    }
  };

  const undoPathPoint = () => setPathPoints(prev => prev.slice(0, -1));
  const clearPath     = () => setPathPoints([]);

  // Auto-generate path from checkpoint order
  const autoPath = () => {
    if (checkpoints.length < 2) return toast.error('Need at least 2 checkpoints');
    const pts = checkpoints.map(cp => [cp.latitude, cp.longitude]);
    setPathPoints(pts);
    toast.success('Path generated from checkpoint order');
  };

  const savePath = async () => {
    const points = pathPoints.length >= 2 ? pathPoints : null;
    const geojson = points ? {
      type: 'LineString',
      coordinates: points.map(p => [p[1], p[0]]),
    } : null;

    setSavingPath(true);
    try {
      await axios.put(`/api/routes/${selectedRoute.id}/path`, { path_geojson: geojson });
      setSavedPath(points || []);
      setPathPoints([]);
      setMode('none');
      toast.success(points ? '✅ Route path saved!' : 'Path cleared');
      // Refresh route list
      load();
    } catch { toast.error('Failed to save path'); }
    setSavingPath(false);
  };

  // ── Drag-to-reorder checkpoints ──────────────────────────────
  const handleDragStart = (id) => setDragging(id);
  const handleDragEnter = (id) => { dragOver.current = id; };
  const handleDragEnd   = async () => {
    if (!dragging || !dragOver.current || dragging === dragOver.current) { setDragging(null); return; }
    const list = [...checkpoints];
    const from = list.findIndex(c => c.id === dragging);
    const to   = list.findIndex(c => c.id === dragOver.current);
    list.splice(to, 0, list.splice(from, 1)[0]);
    const reordered = list.map((c, i) => ({ ...c, sequence_order: i + 1 }));
    setCheckpoints(reordered);
    setDragging(null);
    dragOver.current = null;
    try {
      await axios.put(`/api/routes/${selectedRoute.id}/path`, {
        checkpoint_order: reordered.map(c => c.id),
      });
      toast.success('Order saved');
    } catch { toast.error('Failed to save order'); loadCheckpoints(selectedRoute.id); }
  };

  // ── Create route ─────────────────────────────────────────────
  const handleCreateRoute = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/api/routes', routeForm);
      toast.success('Route created');
      setShowForm(false); setRouteForm(emptyForm);
      load();
    } catch (err) { toast.error(err.response?.data?.error||'Failed'); }
  };

  // ── Computed polyline: checkpoints auto-line if no drawn path ─
  const cpPolyline = checkpoints.filter(cp => cp.latitude && cp.longitude).length >= 2
    ? checkpoints.filter(cp => cp.latitude && cp.longitude).map(cp => [cp.latitude, cp.longitude])
    : [];

  const displayPath = (mode === 'draw_path' && pathPoints.length >= 2) ? pathPoints
    : (pathPoints.length >= 2 ? pathPoints : savedPath);

  const routeColor = selectedRoute?.color || '#4361ee';

  // ────────────────────────────────────────────────────────────
  // ── CHECKPOINT MANAGER VIEW ──────────────────────────────────
  if (selectedRoute) {
    return (
      <div>
        {/* Header */}
        <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <button className="btn btn-secondary btn-sm" onClick={closeRoute}>← Back to Routes</button>
            <div className="page-title" style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', background: routeColor, flexShrink: 0 }} />
              {selectedRoute.name}
            </div>
            <div style={{ color: '#9ca3af', fontSize: 13 }}>
              {selectedRoute.site_name} · {checkpoints.length} checkpoints
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Mode buttons */}
            <button
              onClick={() => { setMode(mode === 'place_cp' ? 'none' : 'place_cp'); setPendingPin(null); }}
              className={mode === 'place_cp' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: 13 }}>
              {mode === 'place_cp' ? '✕ Cancel' : '📍 Add Checkpoint'}
            </button>
            <button
              onClick={() => { setMode(mode === 'draw_path' ? 'none' : 'draw_path'); }}
              className={mode === 'draw_path' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: 13 }}>
              {mode === 'draw_path' ? '✕ Stop Drawing' : '✏️ Draw Route Path'}
            </button>
            <button
              onClick={() => handleDeleteRoute(selectedRoute)}
              disabled={deletingRouteId === selectedRoute.id}
              className="btn btn-danger btn-sm"
              style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
              🗑️ {deletingRouteId === selectedRoute.id ? 'Deleting…' : 'Delete Route'}
            </button>
          </div>
        </div>

        {/* Mode hints — only show for GPS mode */}
        {mode === 'place_cp' && cpForm.scan_type !== 'photo' && (
          <div style={{ background: '#4361ee', color: 'white', borderRadius: 10, padding: '10px 16px', marginBottom: 12, fontWeight: 600, fontSize: 13, textAlign: 'center' }}>
            👆 Click on the map to drop a checkpoint pin
          </div>
        )}
        {mode === 'draw_path' && (
          <div style={{ background: '#f59e0b', color: 'white', borderRadius: 10, padding: '10px 16px', marginBottom: 12, fontWeight: 600, fontSize: 13 }}>
            ✏️ Click on map to draw route path — {pathPoints.length} points added &nbsp;
            <button onClick={undoPathPoint} disabled={pathPoints.length === 0}
              style={{ background: 'rgba(255,255,255,.3)', border: 'none', color: 'white', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', marginRight: 6 }}>↩ Undo</button>
            <button onClick={clearPath}
              style={{ background: 'rgba(255,255,255,.3)', border: 'none', color: 'white', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', marginRight: 6 }}>🗑 Clear</button>
            <button onClick={autoPath}
              style={{ background: 'rgba(255,255,255,.3)', border: 'none', color: 'white', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', marginRight: 6 }}>⚡ Auto from CPs</button>
            <button onClick={savePath} disabled={savingPath}
              style={{ background: 'white', color: '#f59e0b', border: 'none', borderRadius: 6, padding: '3px 12px', cursor: 'pointer', fontWeight: 700 }}>
              {savingPath ? 'Saving…' : '💾 Save Path'}
            </button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

          {/* ── MAP ─────────────────────────────────────────── */}
          <div>
            <SearchBox onResult={({ lat, lng }) => setFlyTarget([lat, lng])} />

            {/* Tile + save path button */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center' }}>
              {Object.values(MAP_TILES).map(t => (
                <button key={t.id} onClick={() => setTile(t.id)}
                  style={{
                    padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 12,
                    background: tile === t.id ? '#4361ee' : '#e5e7eb',
                    color: tile === t.id ? 'white' : '#374151', fontWeight: tile === t.id ? 600 : 400,
                  }}>
                  {t.name}
                </button>
              ))}
              {(savedPath.length >= 2 || pathPoints.length >= 2) && mode !== 'draw_path' && (
                <button onClick={() => { setPathPoints([]); savePath(); }}
                  style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #ef233c', color: '#ef233c', background: 'white', cursor: 'pointer', fontSize: 12 }}>
                  🗑 Clear Path
                </button>
              )}
              {checkpoints.length >= 2 && savedPath.length === 0 && pathPoints.length === 0 && mode !== 'draw_path' && (
                <button onClick={() => { autoPath(); setTimeout(savePath, 200); }}
                  style={{ padding: '5px 10px', borderRadius: 7, border: '1px solid #22c55e', color: '#22c55e', background: 'white', cursor: 'pointer', fontSize: 12 }}>
                  ⚡ Auto-generate path
                </button>
              )}
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 4 }}>
                {displayPath.length >= 2 ? `${displayPath.length} path points` : 'No path drawn yet'}
              </span>
            </div>

            <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.1)' }}>
              <MapContainer center={[20.5937, 78.9629]} zoom={5} maxZoom={22} style={{ height: 500 }}>
                <TileLayer
                  key={tile}
                  url={MAP_TILES[tile]?.url || TILES[tile] || MAP_TILES.googleHybrid.url}
                  attribution={MAP_TILES[tile]?.attr || '© Google Maps'}
                  maxZoom={22}
                  maxNativeZoom={MAP_TILES[tile]?.maxNativeZoom || 21}
                />
                {flyTarget && <FlyTo center={flyTarget} />}
                <MapClick onMapClick={(ll) => handleMapClick(ll)} active={mode !== 'none'} />
                <LocateBtn />

                {/* Saved / drawn route path */}
                {displayPath.length >= 2 && (
                  <Polyline positions={displayPath} color={routeColor} weight={4} opacity={0.8} />
                )}

                {/* Auto CP connection line (dashed, when no path drawn) */}
                {displayPath.length < 2 && cpPolyline.length >= 2 && (
                  <Polyline positions={cpPolyline} color={routeColor} weight={3} opacity={0.4} dashArray="8 6" />
                )}

                {/* Path drawing preview points */}
                {mode === 'draw_path' && pathPoints.map((pt, i) => (
                  <Marker key={i} position={pt} icon={pathDotIcon(i)}>
                    <Popup>Path point {i + 1}</Popup>
                  </Marker>
                ))}

                {/* Checkpoint markers — skip photo CPs with no coords */}
                {checkpoints.filter(cp => cp.latitude && cp.longitude).map(cp => (
                  <React.Fragment key={cp.id}>
                    <Circle
                      center={[cp.latitude, cp.longitude]}
                      radius={cp.radius_meters}
                      color={rColor(cp.radius_meters)}
                      fillOpacity={0.15} weight={2}
                    />
                    <Marker position={[cp.latitude, cp.longitude]} icon={cpIcon(cp.sequence_order, routeColor)}>
                      <Popup>
                        <strong>CP{cp.sequence_order}: {cp.name}</strong><br />
                        Radius: {cp.radius_meters}m<br />
                        {cp.description && <span style={{ color: '#666' }}>{cp.description}</span>}
                        <br />
                        <button onClick={() => deleteCP(cp.id, cp.name)}
                          style={{ marginTop: 6, background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', fontSize: 12 }}>
                          🗑 Delete
                        </button>
                      </Popup>
                    </Marker>
                  </React.Fragment>
                ))}

                {/* Pending new pin */}
                {pendingPin && (
                  <>
                    <Circle center={[pendingPin.lat, pendingPin.lng]} radius={+cpForm.radius_meters || 60}
                      color="#ef233c" fillOpacity={0.2} weight={2} dashArray="8" />
                    <Marker position={[pendingPin.lat, pendingPin.lng]} icon={pendingIcon}>
                      <Popup>New checkpoint — fill form →</Popup>
                    </Marker>
                  </>
                )}
              </MapContainer>
            </div>

            {/* Map legend */}
            <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: '#9ca3af', flexWrap: 'wrap' }}>
              <span style={{ color: routeColor, fontWeight: 600 }}>━ Route path</span>
              <span>● CP marker (click to delete)</span>
              <span style={{ color: rColor(20) }}>● 20m</span>
              <span style={{ color: rColor(60) }}>● 60m</span>
              <span style={{ color: rColor(100) }}>● 100m</span>
              <span style={{ color: '#f59e0b' }}>● Path point</span>
            </div>
          </div>

          {/* ── RIGHT PANEL ──────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Add checkpoint form */}
            {(mode === 'place_cp' || cpForm.scan_type === 'photo') && (
              <div className="card">
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
                  {cpForm.scan_type === 'photo'
                    ? '📷 Photo Only — no location needed'
                    : pendingPin ? '✅ GPS Location set' : '📍 Click map to set GPS location'}
                </div>

                {/* GPS type: show location picker */}
                {cpForm.scan_type === 'gps' && (
                  <>
                    {pendingPin ? (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 7, padding: '7px 10px', marginBottom: 10, fontSize: 12, color: '#166534', display: 'flex', justifyContent: 'space-between' }}>
                        <span>📍 {parseFloat(pendingPin.lat).toFixed(5)}, {parseFloat(pendingPin.lng).toFixed(5)}</span>
                        <button onClick={() => setPendingPin(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button>
                      </div>
                    ) : (
                      <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 7, padding: '8px 10px', marginBottom: 10, fontSize: 12, color: '#92400e', textAlign: 'center', fontWeight: 600 }}>
                        ⚠️ Click on the map to set GPS location — required for GPS type
                      </div>
                    )}

                    {/* Manual coords */}
                    <div style={{ background: '#f0f4ff', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Latitude</div>
                          <input className="form-input" style={{ fontSize: 12, padding: '6px 9px' }}
                            placeholder="23.0225" value={cpForm.lat}
                            onChange={e => setCpForm({ ...cpForm, lat: e.target.value })} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 3 }}>Longitude</div>
                          <input className="form-input" style={{ fontSize: 12, padding: '6px 9px' }}
                            placeholder="72.5714" value={cpForm.lng}
                            onChange={e => setCpForm({ ...cpForm, lng: e.target.value })} />
                        </div>
                      </div>
                      <button type="button" className="btn btn-secondary btn-sm" style={{ width: '100%' }} onClick={applyManual}>
                        📌 Place from coordinates
                      </button>
                    </div>
                  </>
                )}

                {/* Photo type: show info banner */}
                {cpForm.scan_type === 'photo' && (
                  <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 7, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#4338ca' }}>
                    📷 Guard will take a photo as proof of visit. No GPS coordinates needed.
                  </div>
                )}

                <form onSubmit={handleAddCP}>
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input className="form-input" placeholder="e.g. Main Gate" required
                      value={cpForm.name} onChange={e => setCpForm({ ...cpForm, name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guard note</label>
                    <input className="form-input" placeholder="e.g. Near elevator, 7th floor"
                      value={cpForm.description} onChange={e => setCpForm({ ...cpForm, description: e.target.value })} />
                  </div>
                  {/* Detection Radius — only needed for GPS type */}
                  {cpForm.scan_type === 'gps' && (
                  <div className="form-group">
                    <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Detection Radius</span>
                      <strong style={{ color: rColor(cpForm.radius_meters) }}>{cpForm.radius_meters}m</strong>
                    </label>
                    <input type="range" min="5" max="200" step="5" value={Math.min(cpForm.radius_meters, 200)}
                      onChange={e => setCpForm({ ...cpForm, radius_meters: +e.target.value })}
                      style={{ width: '100%', accentColor: rColor(cpForm.radius_meters), marginBottom: 6 }} />
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                      <input type="number" min="5" step="5" className="form-input"
                        style={{ width: 100, fontSize: 13, padding: '5px 8px' }}
                        value={cpForm.radius_meters}
                        onChange={e => {
                          const v = Math.max(5, +e.target.value || 5);
                          setCpForm({ ...cpForm, radius_meters: v });
                        }} />
                      <span style={{ fontSize: 12, color: '#64748b' }}>metres — min 5m, no upper limit</span>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      {[5, 10, 20, 30, 50, 100, 150, 200].map(r => (
                        <button key={r} type="button"
                          onClick={() => setCpForm({ ...cpForm, radius_meters: r })}
                          style={{
                            padding: '3px 9px', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                            border: `1.5px solid ${cpForm.radius_meters === r ? '#4361ee' : '#e5e7eb'}`,
                            background: cpForm.radius_meters === r ? '#eef2ff' : 'white',
                            color: cpForm.radius_meters === r ? '#4361ee' : '#6b7280',
                            fontWeight: cpForm.radius_meters === r ? 700 : 400,
                          }}>{r}m</button>
                      ))}
                    </div>
                  </div>
                  )}
                  <div className="form-group">
                    <label className="form-label">Scan Type</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <button type="button"
                        onClick={() => setCpForm({ ...cpForm, scan_type: 'photo' })}
                        style={{
                          padding: '8px 6px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                          border: `2px solid ${cpForm.scan_type === 'photo' ? '#4361ee' : '#e5e7eb'}`,
                          background: cpForm.scan_type === 'photo' ? '#eef2ff' : 'white',
                          color: cpForm.scan_type === 'photo' ? '#4361ee' : '#6b7280',
                          fontWeight: cpForm.scan_type === 'photo' ? 700 : 400,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        }}>
                        <span style={{ fontSize: 18 }}>📷</span>
                        Photo Only
                        <span style={{ fontSize: 9, opacity: .7 }}>Indoor � no GPS needed</span>
                      </button>
                      <button type="button"
                        onClick={() => setCpForm({ ...cpForm, scan_type: 'gps' })}
                        style={{
                          padding: '8px 6px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                          border: `2px solid ${cpForm.scan_type === 'gps' ? '#22c55e' : '#e5e7eb'}`,
                          background: cpForm.scan_type === 'gps' ? '#f0fdf4' : 'white',
                          color: cpForm.scan_type === 'gps' ? '#16a34a' : '#6b7280',
                          fontWeight: cpForm.scan_type === 'gps' ? 700 : 400,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                        }}>
                        <span style={{ fontSize: 18 }}>📡</span>
                        GPS Only
                        <span style={{ fontSize: 9, opacity: .7 }}>Outdoor / Open Area</span>
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Order #</label>
                    <input type="number" className="form-input" min="1" value={cpForm.sequence_order}
                      onChange={e => setCpForm({ ...cpForm, sequence_order: +e.target.value })} />
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: '100%' }}
                    disabled={(cpForm.scan_type === 'gps' && !pendingPin) || addingCP || !cpForm.name}>
                    {addingCP ? '⏳ Saving…' : '+ Add Checkpoint'}
                  </button>
                </form>
              </div>
            )}

            {/* Checkpoints list — drag to reorder */}
            <div className="card">
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                <span>Checkpoints ({checkpoints.length})</span>
                {checkpoints.length >= 2 && (
                  <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>⠿ drag to reorder</span>
                )}
              </div>

              {checkpoints.length === 0 ? (
                <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                  Click "📍 Add Checkpoint" and place pins on the map
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {checkpoints.map((cp) => (
                    <div
                      key={cp.id}
                      draggable
                      onDragStart={() => handleDragStart(cp.id)}
                      onDragEnter={() => { dragOver.current = cp.id; }}
                      onDragEnd={handleDragEnd}
                      onDragOver={e => e.preventDefault()}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
                        borderRadius: 8, border: `1px solid ${dragging === cp.id ? '#4361ee' : '#e5e7eb'}`,
                        background: dragging === cp.id ? '#eef2ff' : 'white',
                        cursor: 'grab', userSelect: 'none',
                      }}
                    >
                      <span style={{ color: '#d1d5db', fontSize: 16, flexShrink: 0 }}>⠿</span>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        background: routeColor, color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                      }}>{cp.sequence_order}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cp.name}
                        </div>
                        <div style={{ fontSize: 10, color: '#9ca3af' }}>
                          <span style={{ color: rColor(cp.radius_meters), fontWeight: 600 }}>{cp.radius_meters}m</span>
                          &nbsp;·&nbsp;{cp.latitude && cp.longitude ? `${(+cp.latitude).toFixed(4)}, ${(+cp.longitude).toFixed(4)}` : '📷 indoor'}
                          &nbsp;·&nbsp;
                          <span style={{
                            fontWeight: 700,
                            color: cp.scan_type === 'gps' ? '#16a34a' : '#4361ee',
                          }}>
                            {cp.scan_type === 'gps' ? '📡 GPS' : '📷 Photo'}
                          </span>
                        </div>
                      </div>
                      <button onClick={() => { if (cp.latitude && cp.longitude) setFlyTarget([cp.latitude, cp.longitude]); }}
                        title="Fly to" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2 }}>🎯</button>
                      <button onClick={() => deleteCP(cp.id, cp.name)}
                        title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 14, padding: 2 }}>🗑</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Quick actions */}
              {checkpoints.length >= 2 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
                  <button onClick={autoPath} className="btn btn-secondary btn-sm" style={{ flex: 1 }}>
                    ⚡ Auto Path
                  </button>
                  {pathPoints.length >= 2 && (
                    <button onClick={savePath} disabled={savingPath} className="btn btn-primary btn-sm" style={{ flex: 1 }}>
                      {savingPath ? '…' : '💾 Save Path'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────
  // ── ROUTES OVERVIEW ──────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div className="page-title">Patrol Routes</div>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>+ New Route</button>
      </div>

      {/* Create route form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Create New Route</div>
          <form onSubmit={handleCreateRoute}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div className="form-group">
                <label className="form-label">Site *</label>
                <select className="form-input" required value={routeForm.site_id}
                  onChange={e => setRouteForm({ ...routeForm, site_id: e.target.value })}>
                  <option value="">Select site…</option>
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Route Name *</label>
                <input className="form-input" placeholder="e.g. Night Patrol A" required
                  value={routeForm.name} onChange={e => setRouteForm({ ...routeForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Duration (mins)</label>
                <input type="number" className="form-input" value={routeForm.expected_duration_minutes}
                  onChange={e => setRouteForm({ ...routeForm, expected_duration_minutes: +e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-input" value={routeForm.description}
                  onChange={e => setRouteForm({ ...routeForm, description: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Route Color</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {ROUTE_COLORS.map(c => (
                    <div key={c} onClick={() => setRouteForm({ ...routeForm, color: c })}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', background: c, cursor: 'pointer',
                        border: routeForm.color === c ? '3px solid #1a1a2e' : '2px solid white',
                        boxShadow: '0 1px 4px rgba(0,0,0,.2)',
                      }} />
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button type="submit" className="btn btn-primary">Save Route</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Overview map — all routes with paths */}
      {routes.filter(r => r.path_geojson).length > 0 && (
        <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', fontWeight: 700, fontSize: 14, borderBottom: '1px solid #e5e7eb' }}>
            🗺️ All Routes Overview
          </div>
          <MapContainer center={[20.5937, 78.9629]} zoom={5} maxZoom={22} style={{ height: 300 }}>
            <TileLayer url={MAP_TILES.googleHybrid.url} attribution="© Google Maps" maxZoom={22} maxNativeZoom={21} />
            {routes.filter(r => r.path_geojson).map(r => {
              try {
                const geo = typeof r.path_geojson === 'string' ? JSON.parse(r.path_geojson) : r.path_geojson;
                const pts = geo?.coordinates?.map(c => [c[1], c[0]]) || [];
                if (pts.length < 2) return null;
                return (
                  <React.Fragment key={r.id}>
                    <Polyline positions={pts} color={r.color || '#4361ee'} weight={4} opacity={0.8}>
                      <Popup><strong>{r.name}</strong><br />{r.site_name}</Popup>
                    </Polyline>
                    {/* Start marker */}
                    <Marker position={pts[0]} icon={cpIcon('S', r.color || '#4361ee')}>
                      <Popup>Start: {r.name}</Popup>
                    </Marker>
                  </React.Fragment>
                );
              } catch { return null; }
            })}
          </MapContainer>
          {/* Legend */}
          <div style={{ padding: '8px 16px', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12 }}>
            {routes.filter(r => r.path_geojson).map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 24, height: 4, borderRadius: 2, background: r.color || '#4361ee' }} />
                <span style={{ color: '#374151' }}>{r.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Routes table */}
      <div className="card">
        {loading ? <div style={{ padding: 20 }}>Loading…</div> : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr><th>Color</th><th>Route</th><th>Site</th><th>Checkpoints</th><th>Path</th><th>Duration</th><th>Status</th><th>Action</th></tr>
              </thead>
              <tbody>
                {routes.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>
                    No routes yet. Click "+ New Route" to create one.
                  </td></tr>
                )}
                {routes.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', background: r.color || '#4361ee' }} />
                    </td>
                    <td>
                      <strong>{r.name}</strong>
                      {r.description && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.description}</div>}
                    </td>
                    <td>{r.site_name}</td>
                    <td>
                      <span className={`badge ${r.checkpoint_count > 0 ? 'badge-info' : 'badge-gray'}`}>
                        {r.checkpoint_count} pts
                      </span>
                    </td>
                    <td>
                      {r.path_geojson
                        ? <span className="badge badge-success">✅ Drawn</span>
                        : <span className="badge badge-gray">No path</span>
                      }
                    </td>
                    <td>{r.expected_duration_minutes} min</td>
                    <td>
                      <span className={`badge ${r.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button className="btn btn-sm btn-primary" onClick={() => openRoute(r)}>
                          📍 Manage
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteRoute(r)}
                          disabled={deletingRouteId === r.id}
                          title="Delete route"
                          style={{ padding: '6px 10px' }}
                        >
                          {deletingRouteId === r.id ? '…' : '🗑️ Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
